import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Standardised top-bar for every customer-facing page.
 *
 * Uses the BBA navy (#1A1A2E) + white Oswald typography that the design
 * spec calls for. Pages should mount it as the very first element so the
 * status bar / mobile safe-area behaves correctly.
 *
 * Props:
 *   title    — the heading text (rendered uppercase)
 *   subtitle — optional small line underneath (e.g. "Step 2 of 3")
 *   showBack — render the ← back-button (defaults to true)
 *   onBack   — override the default `navigate(-1)` behaviour if needed
 *   right    — optional React node rendered on the far right
 */
const CustomerHeader = ({ title, subtitle, showBack = true, onBack, right }) => {
  const navigate = useNavigate();
  const handleBack = onBack || (() => navigate(-1));

  return (
    <div
      className="customer-page-header"
      data-testid="customer-header"
      style={{
        background: '#0057A8',
        color: '#FFFFFF',
        /* padding-top accounts for iPhone notch / Dynamic Island */
        paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
        paddingBottom: '16px',
        paddingLeft: '16px',
        paddingRight: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontFamily: 'Oswald, sans-serif',
        letterSpacing: '1px',
      }}
    >
      {showBack && (
        <button
          type="button"
          onClick={handleBack}
          data-testid="customer-header-back"
          aria-label="Go back"
          style={{
            background: 'none',
            border: 'none',
            color: '#FFFFFF',
            cursor: 'pointer',
            fontSize: '22px',
            lineHeight: 1,
            /* 44×44 touch target minimum */
            minHeight: '44px',
            minWidth: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            padding: 0,
            marginLeft: '-8px',
          }}
        >
          ←
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '18px',
            fontWeight: 700,
            textTransform: 'uppercase',
            lineHeight: 1.15,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: '12px',
              fontWeight: 400,
              opacity: 0.75,
              marginTop: '2px',
              letterSpacing: '0.5px',
              textTransform: 'none',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {right ? <div style={{ flexShrink: 0 }}>{right}</div> : null}
    </div>
  );
};

export default CustomerHeader;
